/**
 * DTOs espelhando as responses do morpherd-api.
 * Contrato canonico: ../../../integration-plan/context/api-contracts.md
 */

// ── Pagination ────────────────────────────────────────────────────────────────

export interface Paginated<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// ── Auth / User ───────────────────────────────────────────────────────────────

export interface User {
  id: number
  name: string
  email: string
  /** System admin. Only ever returned by `/api/auth/me/`, never by member listings. */
  is_superuser?: boolean
  /** Platform staff. Only returned by `/api/auth/me/`. */
  is_staff?: boolean
  /** Email confirmed. Like `is_superuser`, only ever returned by `/api/auth/me/`. */
  email_verified?: boolean
}

/** Runtime feature flags served by `GET /api/config/`. */
export interface AppConfig {
  billing_enabled: boolean
}

export type WorkspaceRole = 1 | 2 | 3 // OWNER | ADMIN | EMPLOYEE

export const WorkspaceRoleValue = {
  OWNER: 1,
  ADMIN: 2,
  EMPLOYEE: 3,
} as const

export type ProjectRole = 1 | 2 | 3 | 4 // OWNER | ADMIN | EDITOR | VIEWER

export const ProjectRoleValue = {
  OWNER: 1,
  ADMIN: 2,
  EDITOR: 3,
  VIEWER: 4,
} as const

export interface Membership {
  workspace_id: number
  workspace_name: string
  workspace_slug?: string
  role: WorkspaceRole
}

export interface MeResponse extends User {
  memberships: Membership[]
  email_verified: boolean
}

// ── Email confirmation / password recovery ────────────────────────────────────

/** Answer of every endpoint that puts a one-time code in the user's mailbox. */
export interface EmailCodeResponse {
  /** Seconds until the code stops working. */
  expires_in: number
  /** Seconds the user has to wait before asking for another one. */
  resend_in: number
  email?: string
}

export interface VerifyEmailRequest {
  code: string
}

export interface PasswordResetRequest {
  email: string
}

export interface PasswordResetConfirmRequest {
  email: string
  code: string
  password: string
}

export interface RegisterRequest {
  name: string
  email: string
  password: string
}

export interface RegisterResponse {
  user: User
  workspace: { id: number; name: string }
}

export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  user: User
}

// ── Workspaces ────────────────────────────────────────────────────────────────

export interface Workspace {
  id: number
  name: string
  slug: string
  is_active: boolean
  is_personal?: boolean
  my_role?: WorkspaceRole | null
  members?: WorkspaceMember[]
  member_count?: number
  subscription_summary?: SubscriptionSummary | null
}

export interface WorkspaceMember {
  id: number
  user: User
  role: WorkspaceRole
  is_active: boolean
  created_at: string
}

export interface CreateWorkspaceRequest {
  name: string
  slug: string
  plan_code?: string
}

export interface PaidWorkspaceCheckoutRequest {
  name: string
  slug: string
  plan_code: string
}

export interface PaidWorkspaceCheckoutResponse {
  checkout_url: string
  pending_checkout_id: number
}

export interface InviteMemberRequest {
  email: string
  role: WorkspaceRole
}

export interface UpdateMemberRequest {
  role?: WorkspaceRole
  is_active?: boolean
}

// ── Plans / Subscription ──────────────────────────────────────────────────────

export interface Plan {
  id: number
  name: string
  code: string
  description?: string
  max_projects: number | null
  max_members: number | null
  max_history_per_project?: number | null
  allows_team?: boolean
  monthly_price_cents?: number
  allow_version_history?: boolean
  allow_team_members?: boolean
  allow_export_import?: boolean
  price_cents: number
  is_available_for_purchase?: boolean
  is_active: boolean
}

export type SubscriptionStatus = 'free' | 'active' | 'trialing' | 'past_due' | 'canceled'

export interface Subscription {
  id: number
  plan: Plan
  status: SubscriptionStatus
  /** Immutable list price contracted for this subscription, before discounts. */
  price_cents: number | null
  provider_price_id?: string | null
  current_period_start: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  canceled_at?: string | null
  /** Masked CPF/CNPJ (e.g. "***.***.**9-10"); full document is never returned. */
  document_masked?: string | null
  /** Plan the subscription will switch to (scheduled downgrade), if any. */
  pending_plan_code?: string | null
  /** When the scheduled plan change takes effect. */
  scheduled_change_at?: string | null
  /** Set when a payment failed (PAST_DUE); drives the "update your card" banner. */
  past_due_since?: string | null
}

export interface ChargeHistoryItem {
  id: number
  amount_cents: number
  currency: string
  status: 'pending' | 'paid' | 'failed' | 'expired'
  plan_code: string | null
  plan_name: string
  billing_period_start: string | null
  billing_period_end: string | null
  paid_at: string | null
  created_at: string
}

// ── Persistent announcements ────────────────────────────────────────────────

export type AnnouncementSeverity = 'info' | 'warning' | 'critical'

export interface AnnouncementContent {
  id: number
  title: string
  body: string
  severity: AnnouncementSeverity
  cta_text: string | null
  cta_url: string | null
  starts_at: string
  ends_at: string | null
  is_test?: boolean
}

export interface AnnouncementDelivery {
  id: number
  announcement: AnnouncementContent
  seen_at: string | null
  dismissed_at: string | null
  created_at: string
}

export interface AdminAnnouncement {
  id: number
  title: string
  body: string
  severity: 1 | 2 | 3
  severity_name: AnnouncementSeverity
  cta_text: string | null
  cta_url: string | null
  starts_at: string | null
  ends_at: string | null
  scheduled_for: string | null
  sent_at: string | null
  status: 1 | 2 | 3
  status_name: 'draft' | 'scheduled' | 'sent'
  audience_type: 1 | 2
  target_workspace: number | null
  segment_type: number | null
  segment_params: Record<string, unknown>
  author_name: string
  audience_count: number | null
  is_active: boolean
  created_at: string
}

export interface AnnouncementAudiencePreview {
  count: number
  fingerprint: string
  summary: Record<string, Record<string, number>>
}

export interface AnnouncementMetrics {
  delivered: number
  seen: number
  dismissed: number
  unseen: number
  seen_percent: number
  dismissed_percent: number
}

// ── Platform administration ─────────────────────────────────────────────────

export interface AdminRiskRow {
  workspace_id: number
  workspace_name: string
  workspace_slug: string
  owner_names: string[]
  owner_emails: string[]
  plan_code: string
  status: string
  monthly_value_cents: number | null
  contract_complete: boolean
  urgency: number
  risk_score: number
  signals: string[]
  deadline: string | null
  last_edit_activity_at: string | null
  workspace_created_at: string
}

export interface AdminAccountOverview {
  workspace: { id: number; name: string; slug: string; created_at: string }
  subscription: {
    id: number
    plan_code: string
    plan_name: string
    status: string
    price_cents: number | null
    contract_complete: boolean
    provider_price_id: string | null
    current_period_start: string | null
    current_period_end: string | null
    cancel_at_period_end: boolean
    pending_plan_code: string | null
    scheduled_change_at: string | null
  }
  usage: {
    projects: number
    max_projects: number
    members: number
    max_members: number
    active_editors_30d: number
    last_edit_activity_at: string | null
  }
  members: Array<{
    id: number
    name: string
    email: string
    role: string
    email_verified: boolean
    last_edit_activity_at: string | null
  }>
  discount: Record<string, unknown> | null
  discount_status: string
}

export interface AdminMetricsSnapshot {
  period: { start: string; end: string }
  gross_mrr_cents: number
  net_mrr_cents: number | null
  net_mrr_status: string
  incomplete_contracts: number
  mrr_at_risk_cents: number
  paid_revenue_cents: number
  total_users: number
  active_users: number
  total_workspaces: number
  active_workspaces: number
  by_plan: Record<string, { subscriptions: number; mrr_cents: number }>
  new_workspaces_by_week: Array<{ week: string; count: number }>
  new_paying_workspaces: number
  distribution: Array<{ plan__code: number; status: number; count: number }>
  funnel: Record<string, number>
}

export interface AdminUserSummary {
  id: number
  name: string
  email: string
  is_active: boolean
  email_verified: boolean
  is_staff: boolean
  is_superuser: boolean
  last_login: string | null
  created_at: string
  workspace_count: number
  active_workspace_count: number
}

export interface AdminUserDetail extends AdminUserSummary {
  workspaces: Array<{
    id: number
    name: string
    slug: string
    is_active: boolean
    membership_active: boolean
    role: string
    plan_code: string | null
    subscription_status: string | null
  }>
}

/** Response of the change-plan endpoint: a hosted checkout URL (when contracting/
 *  upgrading a paid plan) plus the (possibly unchanged) subscription. */
export interface ChangePlanResult {
  checkout_url: string | null
  /** True quando a troca depende de um pagamento que ainda não concluiu (upgrade
   *  com 3DS): o plano só vale quando o webhook confirmar. */
  pending_confirmation: boolean
  subscription: Subscription
}

export interface SubscriptionSummary {
  plan_code: string
  status: SubscriptionStatus
}

export interface ChangePlanRequest {
  /** Backend espera o code do plano (ex.: "free", "team"), não o id. */
  plan_code: string
  payment_provider?: string
  /** CPF/CNPJ exigido ao contratar um plano pago (não é persistido completo). */
  document?: string
}

// ── Projects ──────────────────────────────────────────────────────────────────

export interface ProjectMemberPreview {
  id: number
  role: ProjectRole
  name: string
  email?: string
}

export interface ProjectMember {
  id: number
  project: number
  user: User
  role: ProjectRole
  created_at: string
  modified_at: string
}

export interface ProjectListItem {
  id: number
  name: string
  dialect?: string
  current_commit_id: number | null
  workspace: number
  is_view_only?: boolean
  created_at: string
  modified_at: string
  current_commit?: CommitDetail
  member_count?: number
  member_preview?: ProjectMemberPreview[]
  my_project_role?: ProjectRole | null
}

export interface Project {
  id: number
  name: string
  dialect?: string
  workspace: number
  current_commit_id: number | null
  current_commit?: CommitDetail
  is_active: boolean
  is_view_only?: boolean
  created_at: string
  modified_at: string
  member_count?: number
  member_preview?: ProjectMemberPreview[]
  my_project_role?: ProjectRole | null
}

export interface CreateProjectRequest {
  name: string
  dialect?: string
}

export interface UpdateProjectRequest {
  name?: string
  is_active?: boolean
}

// ── Commits ───────────────────────────────────────────────────────────────────

export interface CommitListItem {
  id: number
  name: string
  description: string
  created_by: User
  created_at: string
  parent_commit: number | null
}

export interface CommitDetail extends CommitListItem {
  metadata: unknown
}

export interface CommitListInner {
  limit: number
  history_truncated_by_plan: boolean
  results: CommitListItem[]
}

export interface CommitListResponse {
  count: number
  next: string | null
  previous: string | null
  results: CommitListInner
}

export interface CreateCommitRequest {
  name: string
  description?: string
  base_commit_id: number | null
}

// ── Drafts ────────────────────────────────────────────────────────────────────

export interface Draft {
  id: number
  project: number
  base_commit: number | null
  last_modified_by?: User | number | null
  rev: number
  metadata: unknown
  is_active: boolean
  created_at: string
  modified_at: string
}

export interface DraftOp {
  op: string
  id?: string
  tableId?: string
  fields?: Record<string, unknown>
  order?: string[]
  x?: number
  y?: number
}

export interface DraftOpsRequest {
  baseRev: number
  ops: DraftOp[]
}

export interface DraftOpsResponse {
  rev: number
  metadata: unknown
}
export interface DraftChangeEntry {
  rev: number
  ops: DraftOp[]
  author: User | null
  created_at: string
}
export interface DraftChangesResponse {
  rev: number
  base_commit: number | null
  /** `false` quando a lacuna não pode ser fechada só com estas ops. */
  complete: boolean
  changes: DraftChangeEntry[]
}

// ── Invitations ───────────────────────────────────────────────────────────────

export type InvitationResponse = 'accepted' | 'declined'

export interface WorkspaceInvitation {
  id: number
  workspace: number
  workspace_name: string
  workspace_slug: string
  invited_by: User
  email: string
  role: WorkspaceRole
  is_active: boolean
  is_pending: boolean
  responded_at: string | null
  response: InvitationResponse | null
  created_at: string
  modified_at: string
}

export interface CreateInvitationRequest {
  email: string
  role: WorkspaceRole
}

export interface RespondInvitationRequest {
  accept: boolean
}

// ── Errors ────────────────────────────────────────────────────────────────────

/**
 * Machine-readable codes emitted by morpherd-api.
 * All error responses follow: { code, description, ...extras? }
 */
export type ApiErrorCode =
  // ── Business / domain ──────────────────────────────────────────────────────
  | 'PROJECT_COMMIT_CONFLICT'
  | 'DRAFT_REVISION_CONFLICT'
  | 'METADATA_MUST_BE_OBJECT'
  | 'METADATA_PAYLOAD_TOO_LARGE'
  | 'BASE_COMMIT_INVALID'
  | 'PROJECT_NAME_DUPLICATE'
  | 'PLAN_PROJECT_LIMIT_REACHED'
  | 'PLAN_MEMBER_LIMIT_REACHED'
  | 'PLAN_MEMBER_NOT_ALLOWED'
  | 'PLAN_FEATURE_NOT_ALLOWED'
  | 'PLAN_DOWNGRADE_NOT_ALLOWED'
  | 'PLAN_EXPORT_IMPORT_NOT_ALLOWED'
  | 'PROJECT_VIEW_ONLY'
  | 'NO_ACTIVE_SUBSCRIPTION'
  | 'SUBSCRIPTION_ALREADY_CANCELED'
  | 'WORKSPACE_LAST_OWNER'
  | 'LEAVE_WORKSPACE_LAST_OWNER'
  | 'REMOVE_MEMBER_LAST_OWNER'
  | 'CANNOT_REMOVE_SELF'
  | 'WORKSPACE_SLUG_INVALID'
  | 'WORKSPACE_SLUG_ALREADY_EXISTS'
  | 'EMAIL_ALREADY_REGISTERED'
  | 'MEMBER_ALREADY_EXISTS'
  | 'ADMIN_CANNOT_PROMOTE_OWNER'
  | 'USER_NOT_FOUND'
  | 'REFRESH_TOKEN_MISSING'
  | 'PROJECT_TRANSFER_SAME_WORKSPACE'
  | 'PROJECT_TRANSFER_NOT_OWNER_SOURCE'
  | 'PROJECT_TRANSFER_NOT_OWNER_TARGET'
  | 'PROJECT_TRANSFER_NAME_CONFLICT'
  | 'PROJECT_TRANSFER_PLAN_LIMIT'
  | 'USER_NOT_WORKSPACE_MEMBER'
  | 'INVITATION_ALREADY_PENDING'
  | 'INVITATION_NOT_FOUND'
  | 'INVITATION_ALREADY_RESPONDED'
  | 'INVITATION_CANCELLED'
  | 'DUPLICATE_PROJECT_MEMBER'
  | 'CANNOT_REMOVE_PROJECT_OWNER'
  | 'PROJECT_OWNER_MUST_BE_UNIQUE'
  | 'PROJECT_MEMBER_NOT_FOUND'
  | 'TARGET_NOT_PROJECT_MEMBER'
  // ── DRF built-ins (normalized to UPPER_SNAKE) ─────────────────────────────
  | 'NOT_FOUND'
  | 'PERMISSION_DENIED'
  | 'NOT_AUTHENTICATED'
  | 'AUTHENTICATION_FAILED'
  | 'TOKEN_NOT_VALID'
  | 'VALIDATION_ERROR'
  | 'METHOD_NOT_ALLOWED'
  | 'THROTTLED'
  // ── HTTP fallbacks ────────────────────────────────────────────────────────
  | `HTTP_${number}`
  | (string & {})

/**
 * Standard error envelope from morpherd-api.
 * Every error response has `code` and `description`.
 * Some business errors include extra payload fields (see CommitConflictError).
 */
export interface ApiErrorBody {
  /** Machine-readable error code. */
  code: ApiErrorCode
  /** Human-readable message to display. Always present. */
  description: string
  /** Kept for backwards-compat during transition; prefer `description`. */
  message?: string
  [extra: string]: unknown
}

export interface CommitConflictError extends ApiErrorBody {
  code: 'PROJECT_COMMIT_CONFLICT'
  current_commit_id: number
  base_commit_id: number
}
